import { getCategoryProgramType, getProgramLogoUrl, PROGRAM_NAMES } from '@/config/programBranding';

// Import category default images
import trainerDefault from '@/assets/defaults/trainer-default.jpg';
import nutritionistDefault from '@/assets/defaults/nutritionist-default.jpg';
import physiotherapistDefault from '@/assets/defaults/physiotherapist-default.jpg';
import yogaDefault from '@/assets/defaults/yoga-default.jpg';
import crossfitDefault from '@/assets/defaults/crossfit-default.jpg';
import gymDefault from '@/assets/defaults/gym-default.jpg';
import pilatesDefault from '@/assets/defaults/pilates-default.jpg';
import danceDefault from '@/assets/defaults/dance-default.jpg';
import martialArtsDefault from '@/assets/defaults/martial-arts-default.jpg';
import genericDefault from '@/assets/defaults/generic-default.jpg';

/**
 * Detects if an image URL is a placeholder or missing
 */
export const isPlaceholderImage = (src: string | null | undefined): boolean => {
  if (!src || src.trim() === '') return true;
  
  const normalized = src.toLowerCase();
  const placeholderPatterns = [
    'default-listing-fallback',
    'default-listing',
    'placeholder',
    'og-image.png',
    '/logos/',
    'via.placeholder.com',
    'picsum.photos',
    'placehold.co',
    'ekselencia_listado_gratuito',
  ];
  
  return placeholderPatterns.some(pattern => normalized.includes(pattern));
};

/**
 * Category-specific default images mapping
 */
const CATEGORY_DEFAULT_IMAGES: Record<string, string> = {
  trainer: trainerDefault,
  nutritionist: nutritionistDefault,
  physiotherapist: physiotherapistDefault,
  yoga: yogaDefault,
  crossfit: crossfitDefault,
  gym: gymDefault,
  pilates: pilatesDefault,
  dance: danceDefault,
  martial_arts: martialArtsDefault,
};

const GENERIC_DEFAULT = genericDefault;

/**
 * Get category-specific default image
 */
export const getCategoryDefaultImage = (category: string): string => {
  const programType = getListingProgramType(category);
  return CATEGORY_DEFAULT_IMAGES[programType] || GENERIC_DEFAULT;
};
 
 /**
  * Maps listing type/business type to program type
  */
 export const getListingProgramType = (listingType: string): string => {
   if (!listingType) return 'trainer';
   
   const normalized = listingType.toLowerCase();
   
   // Direct matches first
   if (PROGRAM_NAMES[normalized]) return normalized;
   
   // Keyword matching
   if (normalized.includes('yoga') || normalized.includes('estudio de yoga')) return 'yoga';
   if (normalized.includes('crossfit') || normalized.includes('box de crossfit')) return 'crossfit';
   if (normalized.includes('gym') || normalized.includes('gimnasio') || normalized.includes('fitness')) return 'gym';
   if (normalized.includes('pilates') || normalized.includes('centro de pilates')) return 'pilates';
   if (normalized.includes('artes marciales') || normalized.includes('boxeo') || normalized.includes('mma') || normalized.includes('karate') || normalized.includes('judo') || normalized.includes('dojo')) return 'martial_arts';
   if (normalized.includes('baile') || normalized.includes('dance') || normalized.includes('academia de baile') || normalized.includes('zumba')) return 'dance';
   if (normalized.includes('nutri') || normalized.includes('nutricionista')) return 'nutritionist';
   if (normalized.includes('fisio') || normalized.includes('fisioterapeuta') || normalized.includes('rehabilitación')) return 'physiotherapist';
   if (normalized.includes('entrenador') || normalized.includes('personal trainer')) return 'trainer';
   
   return 'trainer'; // Default fallback
 };
 
 /**
  * Get logo URL for a category
  */
 export const getCategoryLogoUrl = (category: string): string | undefined => {
   const programType = getListingProgramType(category);
   return getProgramLogoUrl(programType);
 };
 
 /**
  * Determines the effective image source and whether to show logo overlay
  */
 export const getEffectiveImageSource = (
   src: string | null | undefined,
   category: string
 ): { imageSrc: string; showLogo: boolean; logoUrl: string | undefined } => {
   const isPlaceholder = isPlaceholderImage(src);
   const logoUrl = getCategoryLogoUrl(category);
   
   if (isPlaceholder) {
     return {
       imageSrc: getCategoryDefaultImage(category),
       showLogo: true,
       logoUrl,
     };
   }
   
   return {
     imageSrc: src!,
     showLogo: false,
     logoUrl: undefined,
   };
 };